import React from 'react';
import PropTypes from 'prop-types';
import { StyleSheet, css } from 'aphrodite/no-important';
import { connect } from 'react-redux';
import { updateBracket } from '../redux/app';
import Input, { checkboxStyles } from '../components/Fields/Input';
import Select from '../components/Fields/Select';
import Form from './Form';

const s = StyleSheet.create({
  colorSelector: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  colorInput: {
    flexBasis: '49%',
    flexShrink: 0,
  },
  colorIndicator: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    borderRadius: 2,
    display: 'block',
    width: 16,
    height: 16,
  },
});

const COLOR_DARK = 'dark';
const COLOR_LIGHT = 'light';

const THEME_COLOR = [
  {
    value: COLOR_LIGHT,
    label: COLOR_LIGHT,
  },
  {
    value: COLOR_DARK,
    label: COLOR_DARK,
  },
];

const COLOR_SETS = {
  [COLOR_DARK]: {
    color1: '#00162b',
    fontColor: '#fff',
    color3: '#db0a40',
    color4: '#0A86CB',
    backgroundAccents: '#fff',
  },
  [COLOR_LIGHT]: {
    color1: '#f8f8f8',
    fontColor: '#00162b',
    color3: '#062e78',
    color4: '#be0037',
    backgroundAccents: '#00162b',
  },
};

const CURRENT_ROUND_INDEXES = [
  {
    value: 2,
    label: 2,
  },
  {
    value: 4,
    label: 4,
  },
  {
    value: 8,
    label: 8,
  },
  {
    value: 16,
    label: 16,
  },
  {
    value: 32,
    label: 32,
  },
  {
    value: 64,
    label: 64,
  },
  {
    value: 128,
    label: 128,
  },
];

const TREE_SIZES = [
  {
    value: 2,
    label: 2,
  },
  {
    value: 4,
    label: 4,
  },
  {
    value: 8,
    label: 8,
  },
  {
    value: 16,
    label: 16,
  },
  {
    value: 32,
    label: 32,
  },
  {
    value: 64,
    label: 64,
  },
  {
    value: 128,
    label: 128,
  },
];

const ColorIndicator = ({ value }) => (
  <span style={{ backgroundColor: value }} className={css(s.colorIndicator)} />
);

ColorIndicator.propTypes = {
  value: PropTypes.string,
};

const COLOR_FIELDS = [
  {
    name: 'color1',
    __containerClass: s.colorInput,
    indicator: ColorIndicator,
    component: Input,
  },
  {
    name: 'color3',
    __containerClass: s.colorInput,
    indicator: ColorIndicator,
    component: Input,
  },
  {
    name: 'fontColor',
    __containerClass: s.colorInput,
    indicator: ColorIndicator,
    component: Input,
  },
  {
    name: 'color4',
    __containerClass: s.colorInput,
    indicator: ColorIndicator,
    component: Input,
  },
  {
    name: 'backgroundAccents',
    __containerClass: s.colorInput,
    indicator: ColorIndicator,
    component: Input,
  },
];

class EditForm extends Form {
  constructor(props) {
    super(props);

    this.state = {
      values: this.mapProps(),
      didChange: false,
    };
  }

  mapColors = (color) => COLOR_SETS[color];

  mapProps = () => ({
    title: this.props.defaultValues.title || '',
    participantCount: this.props.defaultValues.participantCount || '',
    bracketTitle:
      this.props.defaultValues.bracketTitle || 'bracketeditor.mainTitle',
    currentRoundIndex: this.props.defaultValues.currentRoundIndex,
    location: this.props.defaultValues.location || '',
    color1: this.props.defaultValues.color1 || COLOR_SETS[COLOR_LIGHT].color1,
    fontColor:
      this.props.defaultValues.fontColor || COLOR_SETS[COLOR_LIGHT].fontColor,
    color3: this.props.defaultValues.color3 || COLOR_SETS[COLOR_LIGHT].color3,
    color4: this.props.defaultValues.color4 || COLOR_SETS[COLOR_LIGHT].color4,
    backgroundAccents:
      this.props.defaultValues.backgroundAccents ||
      COLOR_SETS[COLOR_LIGHT].backgroundAccents,
    themeColor: this.props.defaultValues.themeColor || THEME_COLOR[0],
    displayScores: this.props.defaultValues.displayScores || true,
  });

  isValid = () => {
    return !!this.state.values.title && !!this.state.values.participantCount;
  };

  getFields = () => [
    {
      name: 'title',
      component: Input,
      required: true,
    },
    {
      name: 'location',
      component: Input,
    },
    {
      name: 'bracketTitle',
      component: Input,
      required: true,
    },
    {
      name: 'participantCount',
      component: Select,
      options: TREE_SIZES,
      required: true,
      onChange: async (data) => {
        if (this.props.id && this.props.defaultValues.participantCount) {
          return this.props.openConfirm({
            title: {
              messageId: 'app.discard.changes.title',
            },
            message: {
              messageId: 'app.tree.participantCount.change',
            },
            buttons: [
              {
                messageId: 'app.discard.changes.button',
                onClick: (close) => {
                  this.onChange('participantCount')(data);
                  close();
                },
              },
            ],
          });
        }
        this.onChange('participantCount')(data);
      },
    },
    {
      name: 'currentRoundIndex',
      component: Select,
      options: CURRENT_ROUND_INDEXES,
      required: true,
    },
  ];

  nameSpace = 'app.tree.edit';

  render() {
    return (
      <Form.Wrapper>
        {this.createFields(this.getFields())}
        <div className={css(s.checkbox)}>
          {this.createFields([
            {
              name: 'displayScores',
              component: Input,
              type: 'checkbox',
              __containerClass: checkboxStyles.checkboxContainer,
              __inputClass: checkboxStyles.checkboxInput,
            },
          ])}
        </div>
        <div className={css(s.colorSelector)}>
          {this.createFields(COLOR_FIELDS)}
          {this.createFields([
            {
              name: 'themeColor',
              __containerClass: s.colorInput,
              component: Select,
              isColorSelect: true,
              options: THEME_COLOR,
              onChange: (data) => {
                const colorSetColors = this.mapColors(data.value);
                const values = {
                  ...this.state.values,
                  ...colorSetColors,
                  themeColor: { label: data.value, value: data.value },
                };
                this.setState({
                  values,
                  didChange:
                    JSON.stringify(values) !== JSON.stringify(this.mapProps()),
                });
              },
            },
          ])}
        </div>
        <this.Buttons />
      </Form.Wrapper>
    );
  }
}

EditForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  openConfirm: PropTypes.func,
  goToParticipants: PropTypes.func,
  updateBracket: PropTypes.func,
  defaultValues: PropTypes.object,
};

const mapDispatchToProps = Form.mapDispatchToProps((dispatch, ownProps) => ({
  save: async (props) => {
    const { payload } = await dispatch(updateBracket(props, ownProps.id));

    if (!ownProps.id) {
      ownProps.goToParticipants(payload.data.id);
    }
  },
}));

export default connect(undefined, mapDispatchToProps)(EditForm);
